// Zebra Expr — public entry point.
//
//   evaluateExpr(issue, source, options)  -> value (mirrors evaluateFormula's
//                                            signature so dual-mode dispatch is
//                                            a one-liner)
//   compileExpr(source)                   -> AST (cached; throws ExprError)
//   staticAnalyze(source, fieldNameMap)   -> { fields, unknownFunctions, error }
//
// The engine (lexer/parser/interpreter/builtins) is Jira-agnostic; this file is
// the only place that touches real Jira issues, reusing the SAME flattening the
// JS engine uses (flattenIssue / buildFieldNameMap / collectAllDescendants) so
// both engines see identical inputs.

import { parse, AGG_NAMES } from './parser.js';
import { interpret, FN_ALIASES, HIGHER_ORDER } from './interpreter.js';
import { BUILTINS, LAZY, formatDateValue, str } from './builtins.js';
import { buildVariableScope, variablesSignature } from './variables.js';
import { flattenIssue, buildFieldNameMap, evaluateFormula } from '../formulaEngine.js';

// Single dispatch point for evaluating a column's formula with the right engine.
// A formula column carries `formulaLanguage` (tagged from its insight); DSL
// columns go to Zebra Expr, everything else to the existing JS engine. Callers
// that used to call evaluateFormula directly (grid, issue panel, sort, export)
// route through here so no surface ever hands a `sum { … }` formula to the JS
// engine (which throws "Unexpected token '{'" and floods the console).
export function evalColumnFormula(issue, column, ctx = {}) {
  const lang = (column && column.formulaLanguage) || ctx.formulaLanguage || 'js';
  if (lang !== 'dsl') return evaluateFormula(issue, column.formula, ctx);
  // Variables belong to the COLUMN, the way Structure scopes them to the
  // formula that uses them — not to the whole insight. A column that declares
  // none falls back to whatever the caller passed, which is how group / filter
  // / sort rules supply their own.
  const variables = (column && Array.isArray(column.variables) && column.variables.length)
    ? column.variables
    : ctx.variables;
  return evaluateExpr(issue, column.formula, variables === ctx.variables ? ctx : { ...ctx, variables });
}

const _cache = new Map(); // source -> { ast } | { error }

export function compileExpr(source) {
  if (_cache.has(source)) { const c = _cache.get(source); if (c.error) throw c.error; return c.ast; }
  try { const ast = parse(source); _cache.set(source, { ast }); return ast; }
  catch (error) { _cache.set(source, { error }); throw error; }
}

// --- Performance caches ------------------------------------------------------
// DSL columns evaluate client-side, so the same subtree is walked once per cell
// AND re-walked on every React repaint. Two object-identity caches make that
// cheap without any staleness risk: React updates issues/childrenMap immutably
// (a new object on every change — see setContainerChildrenMap), so a changed
// key means a changed object, which misses the cache and recomputes.

// flattenIssue is the hot path (~500 fields parsed per node). Pure in
// (issue, fieldNameMap), so cache per issue object; recompute only if the map
// reference changed.
const _flatCache = new WeakMap(); // issue -> { map, fields }
function flattenCached(issue, fieldNameMap) {
  const hit = _flatCache.get(issue);
  if (hit && hit.map === fieldNameMap) return hit.fields;
  const fields = flattenIssue(issue, fieldNameMap);
  _flatCache.set(issue, { map: fieldNameMap, fields });
  return fields;
}

// Subtree nodes are cached per issue object but only while childrenMap identity
// is unchanged — any structural change (fetch or lazy-expand) replaces the map
// object, so we drop the whole node cache and rebuild fresh.
let _nodeCacheMap = null;
let _nodeCache = new WeakMap(); // issue -> item node
// Final evaluated values, so React repaints don't re-interpret whole subtrees.
// Keyed by the same childrenMap generation: any fetch / lazy-expand / inline
// status change rebuilds containerChildrenMap (new object) → this map is dropped.
let _resultCache = new WeakMap(); // issue -> Map(source -> value)
function resetNodeCacheIfStale(childrenMap) {
  if (childrenMap !== _nodeCacheMap) {
    _nodeCacheMap = childrenMap;
    _nodeCache = new WeakMap();
    _resultCache = new WeakMap();
  }
}

// childKey -> parent issue, derived from a childrenMap and cached on it (a
// changed map is a new object, so the cache can never be stale). This is what
// lets a row see its ANCESTORS: itemify builds downward from the row, so
// without it PARENT{…}, JOIN{…} (root-to-self by default) and #ancestors
// were empty on every screen even though the engine supports them.
const _parentMapCache = new WeakMap(); // childrenMap -> Map(childKey -> parent issue)
function parentMapFor(childrenMap, issuesByKey) {
  let m = _parentMapCache.get(childrenMap);
  if (m) return m;
  m = new Map();
  for (const [parentKey, kids] of Object.entries(childrenMap || {})) {
    const parent = issuesByKey && issuesByKey.get(parentKey);
    for (const k of (kids || [])) if (k && k.key && !m.has(k.key)) m.set(k.key, parent || { key: parentKey, fields: {} });
  }
  _parentMapCache.set(childrenMap, m);
  return m;
}
// The issue objects behind childrenMap's parent keys (a parent is itself
// somebody's child, or a top issue the caller hands in via `topIssues`).
const _issuesByKeyCache = new WeakMap();
function issuesByKeyFor(childrenMap, topIssues) {
  let m = _issuesByKeyCache.get(childrenMap);
  if (m && (!topIssues || m.__top === topIssues)) return m;
  m = new Map();
  for (const kids of Object.values(childrenMap || {})) for (const k of (kids || [])) if (k && k.key) m.set(k.key, k);
  for (const t of (topIssues || [])) if (t && t.key && !m.has(t.key)) m.set(t.key, t);
  m.__top = topIssues;
  _issuesByKeyCache.set(childrenMap, m);
  return m;
}
// Attach the row's ancestor chain. `hops` is how far below the container's
// top rows this row sits (level - baseLevel), so a top row is never given a
// parent even when its Jira parent happens to be in the same container —
// Structure roots the hierarchy at the container's top rows, not at Jira's.
function linkAncestors(root, hops, childrenMap, fieldNameMap, topIssues) {
  if (!hops || hops <= 0) return;
  const byKey = issuesByKeyFor(childrenMap, topIssues);
  const parents = parentMapFor(childrenMap, byKey);
  let cur = root;
  for (let i = 0; i < hops; i++) {
    const parentIssue = parents.get(cur.key);
    if (!parentIssue) break;
    const pnode = itemify(parentIssue, childrenMap, fieldNameMap, 0, cur.level - 1);
    if (pnode === cur) break;
    Object.defineProperty(cur, '__parent', { value: pnode, enumerable: false, configurable: true, writable: true });
    cur = pnode;
  }
}

// Build an item tree rooted at `issue`, walking childrenMap for descendants.
// `level` is the Structure level of THIS node: the caller says what level the
// root sits on (a folder's top work items are Level 1) and each step down adds
// one. Cached per issue like the rest of the node, keyed on the level too so a
// row rendered at a different level cannot serve a stale one.
function itemify(issue, childrenMap, fieldNameMap, depth = 0, level = 1) {
  const cached = _nodeCache.get(issue);
  if (cached && cached._map === fieldNameMap && cached.level === level) return cached;
  const fields = flattenCached(issue, fieldNameMap);
  const kids = (childrenMap[issue.key] || []);
  const children = depth < 20 ? kids.map((k) => itemify(k, childrenMap, fieldNameMap, depth + 1, level + 1)) : [];
  const node = { fields, key: issue.key, children, level, _map: fieldNameMap };
  _nodeCache.set(issue, node);
  return node;
}

// panelToStyled (`:panel[TEXT]{backgroundColor=…}` markup → styled cell) now
// lives in the dependency-free ./panel.js so the server-side DSL evaluator can
// share the exact same logic. Re-exported here so existing importers are
// unchanged.
export { panelToStyled } from './panel.js';
import { panelToStyled } from './panel.js';

// Aggregate decomposition (Formula Store incremental maintenance) — split a
// formula into its aggregation components and re-derive its value from stored
// component values without walking the tree. Additive; see decompose.js.
export { extractAggregates, evalWithAggValues } from './decompose.js';

// Identifiers a formula reads that resolve to neither a field, a WITH binding,
// a function parameter, nor a declared variable — i.e. exactly what an admin
// still has to bind. Powers the editor's "unbound variables" hint.
export function findUnboundNames(source, fieldNameMap = new Map(), variables = []) {
  let ast;
  try { ast = compileExpr(source); } catch { return []; }
  const norm = (n) => String(n).toLowerCase().replace(/[^a-z0-9]/g, '');
  const declared = new Set(
    (Array.isArray(variables) ? variables : []).filter(v => v && v.name).map(v => norm(v.name)),
  );
  const bound = new Set();
  const collect = (n) => {
    if (!n || typeof n !== 'object') return;
    if ((n.kind === 'with' || n.kind === 'withfn') && n.name) bound.add(norm(n.name));
    if (n.kind === 'withfn' || n.kind === 'lambda') for (const p of (n.params || [])) bound.add(norm(p));
    for (const k of Object.keys(n)) {
      const v = n[k];
      if (Array.isArray(v)) v.forEach(collect); else if (v && typeof v === 'object') collect(v);
    }
  };
  collect(ast);
  const out = new Map();
  const walk = (n) => {
    if (!n || typeof n !== 'object') return;
    // `$` / `$1` … are bound by the call they sit in (FILTER, MAP, …), never
    // by the admin.
    if (n.kind === 'field' && n.name && !String(n.name).startsWith('$')) {
      const nn = norm(n.name);
      if (!bound.has(nn) && !declared.has(nn)
          && !fieldNameMap.has(n.name) && !fieldNameMap.has(nn) && !IMPLICIT_NAMES.has(nn)) {
        out.set(nn, n.name);
      }
    }
    for (const k of Object.keys(n)) {
      const v = n[k];
      if (Array.isArray(v)) v.forEach(walk); else if (v && typeof v === 'object') walk(v);
    }
  };
  walk(ast);
  return [...out.values()];
}

// Names the engine supplies itself, so they are never "unbound".
// `statuscategory` is not a field the /fields endpoint advertises: flattenIssue
// derives it from status.statusCategory.name (both engines), so a formula
// copied from Structure that reads `statuscategory = "Done"` evaluates fine
// and must not be flagged as needing a variable.
const IMPLICIT_NAMES = new Set([
  'level', 'depth', 'key', 'itemtype', 'itemid', 'this',
  'today', 'now', 'true', 'false', 'null',
  'statuscategory',
]);

export function evaluateExpr(issue, source, options = {}) {
  if (!source) return '';
  const {
    children = [],
    jiraFields = [],
    fieldNameMap = buildFieldNameMap(jiraFields),
    childrenMap,
    variables,
    // Structure level of `issue`. Defaults to 1 — a folder's top work items —
    // which is what the sorters call Level 1 too.
    level = 1,
    // The level of the container's TOP rows (1 + the group tiers above them).
    // level - baseLevel is how many ancestors the row has inside the container.
    baseLevel = 1,
    // The container's top issues, so a parent that is a top row (not itself
    // anybody's child in childrenMap) still resolves to its real fields.
    topIssues = null,
  } = options;
  // Prefer a full childrenMap (real subtree); fall back to direct children.
  const cmap = childrenMap || { [issue?.key]: children };
  resetNodeCacheIfStale(cmap);

  // Return a memoised value if this exact (issue, formula) was already computed
  // for the current childrenMap generation. Repaints then cost a Map lookup.
  // Variables participate in the cache key: editing a binding must not serve a
  // value computed under the old one.
  const varsSig = variablesSignature(variables);
  // The level participates in the key: the same formula on the same issue can
  // legitimately differ between levels (`if(level = 1, …)`).
  const cacheKey = `${source}\u0000${varsSig}\u0000${level}\u0000${baseLevel}`;
  const canCache = issue && typeof issue === 'object';
  if (canCache) {
    const bySource = _resultCache.get(issue);
    if (bySource && bySource.has(cacheKey)) return bySource.get(cacheKey);
  }

  let display;
  try {
    const ast = compileExpr(source);
    const root = itemify(issue || { key: '', fields: {} }, cmap, fieldNameMap, 0, level);
    linkAncestors(root, level - baseLevel, cmap, fieldNameMap, topIssues);
    const result = panelToStyled(
      interpret(ast, root, buildVariableScope(variables, { parse })),
    );
    if (result === undefined || result === null) display = '';
    else if (typeof result === 'number') display = Number.isNaN(result) ? '' : result;
    else if (typeof result === 'object' && 'text' in result) display = result; // styled cell
    // A date result reads as a date (yyyy-MM-dd), not as String(date)'s
    // "Thu Sep 17 2026 00:00:00 GMT+0000 (…)". Durations already format
    // themselves through toString.
    else if (result instanceof Date) display = formatDateValue(result);
    // A list reads as its members, comma-joined (engine str()), not "a,,b".
    else if (Array.isArray(result)) display = str(result);
    else display = String(result);
  } catch (err) {
    console.error('Zebra Expr error:', err.message);
    display = 'Error';
  }

  if (canCache) {
    let bySource = _resultCache.get(issue);
    if (!bySource) { bySource = new Map(); _resultCache.set(issue, bySource); }
    bySource.set(cacheKey, display);
  }
  return display;
}

// Roll-up / total for a DSL formula over an explicit, already-flattened set of
// issues (the container/group total row passes the full descendant list — see
// collectWithChildren). The issues are treated as flat leaves under a synthetic
// root, so a bare `sum { … }` (subtree scope) counts each once — matching the JS
// engine's synthetic-parent aggregate. Returns the RAW value (or a styled
// { text, … } cell) so the caller applies its own formatting.
//
// Deliberately bypasses the row caches: the synthetic root is a one-off, and
// reusing childrenMap identity as the cache key would let this call clobber the
// per-row cache generation. flattenCached (a permanent WeakMap) is still reused,
// so the expensive per-issue field flattening stays cached.
export function evaluateAggregate(source, issues = [], options = {}) {
  if (!source) return null;
  const { jiraFields = [], fieldNameMap = buildFieldNameMap(jiraFields) } = options;
  try {
    const ast = compileExpr(source);
    const children = issues.map((iss) => ({ fields: flattenCached(iss, fieldNameMap), key: iss.key, children: [] }));
    const root = { fields: {}, key: '__aggregate__', children };
    const result = panelToStyled(interpret(ast, root));
    if (typeof result === 'number' && Number.isNaN(result)) return null;
    if (result instanceof Date) return formatDateValue(result);
    return result;
  } catch (err) {
    console.error('Zebra Expr aggregate error:', err.message);
    return 'Error';
  }
}

// Walk the AST to find every field reference (for the column field-list) and
// flag unknown functions (for save-time validation).
export function staticAnalyze(source, fieldNameMap = new Map()) {
  const result = { fields: new Set(), unknownFunctions: new Set(), error: null };
  let ast;
  try { ast = compileExpr(source); }
  catch (e) { result.error = e; return result; }

  // Collect user-defined function names (WITH f(args) = …) so calls to them
  // aren't reported as unknown.
  const userFns = new Set();
  const collectFns = (node) => {
    if (!node || typeof node !== 'object') return;
    if (node.kind === 'withfn') userFns.add(String(node.name).toUpperCase());
    for (const k of Object.keys(node)) {
      const v = node[k];
      if (Array.isArray(v)) v.forEach(collectFns);
      else if (v && typeof v === 'object') collectFns(v);
    }
  };
  collectFns(ast);

  const known = (name) => BUILTINS[name] || LAZY.has(name) || HIGHER_ORDER.has(name) || FN_ALIASES[name] ||
    userFns.has(name) || AGG_NAMES.has(name);
  const norm = (n) => String(n).toLowerCase().replace(/[^a-z0-9]/g, '');

  const walk = (node) => {
    if (!node || typeof node !== 'object') return;
    if (node.kind === 'field') {
      const id = fieldNameMap.get(node.name) || fieldNameMap.get(norm(node.name));
      if (id) result.fields.add(id);
    }
    if (node.kind === 'call' && !known(node.name)) result.unknownFunctions.add(node.name);
    for (const k of Object.keys(node)) {
      const v = node[k];
      if (Array.isArray(v)) v.forEach(walk);
      else if (v && typeof v === 'object') walk(v);
    }
  };
  walk(ast);
  return result;
}
